import { useEffect, useRef, useState } from 'react';

import type { FieldProposal } from '../../types';

/**
 * Annotation tooltip — ⓘ icon + popover surfacing the per-FieldProposal
 * metadata (confidence, materiality, reason, evidence count) that the
 * backend already returns but the UI hasn't surfaced.
 *
 * Spec: docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §6.2.
 *
 * Renders nothing when none of the annotation fields are populated —
 * caller can render unconditionally without an `if` ladder.
 */

interface AnnotationTooltipProps {
  field: FieldProposal;
  /** Class for the wrapping <span> (positioning). */
  wrapperClassName?: string;
  /** Class for the ⓘ icon itself. */
  iconClassName?: string;
  /** Class for the popover content. */
  popoverClassName?: string;
  /** Class for each row inside the popover. */
  rowClassName?: string;
  /** Class for the row label. */
  labelClassName?: string;
}

function _hasAnyAnnotation(field: FieldProposal): boolean {
  return (
    !!field.confidence ||
    !!field.materiality ||
    !!field.reason ||
    (field.evidence_event_ids?.length ?? 0) > 0
  );
}

export function AnnotationTooltip({
  field,
  wrapperClassName,
  iconClassName,
  popoverClassName,
  rowClassName,
  labelClassName,
}: AnnotationTooltipProps) {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement | null>(null);

  // Close on outside click when pinned.
  useEffect(() => {
    if (!pinned) return undefined;
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setPinned(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pinned]);

  if (!_hasAnyAnnotation(field)) return null;

  const open = pinned || hovered;

  return (
    <span
      ref={wrapperRef}
      className={wrapperClassName}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        className={iconClassName}
        aria-label="Field annotations"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setPinned((p) => !p);
        }}
      >
        ⓘ
      </button>
      {open && (
        <span className={popoverClassName} role="tooltip">
          {field.confidence && (
            <span className={rowClassName}>
              <span className={labelClassName}>Confidence</span>
              <span>{field.confidence}</span>
            </span>
          )}
          {field.materiality && (
            <span className={rowClassName}>
              <span className={labelClassName}>Materiality</span>
              <span>{field.materiality}</span>
            </span>
          )}
          {field.reason && (
            <span className={rowClassName}>
              <span className={labelClassName}>Reason</span>
              <span>{field.reason}</span>
            </span>
          )}
          {field.evidence_event_ids?.length > 0 && (
            <span className={rowClassName}>
              <span className={labelClassName}>Evidence</span>
              <span>{field.evidence_event_ids.length} event(s)</span>
            </span>
          )}
        </span>
      )}
    </span>
  );
}
